import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, UserX, UserCheck, Trash2, X, Shield, User, Lock, Unlock } from 'lucide-react';

const ROLE_COLORS = {
  superadmin: 'bg-red-900/40 text-red-400 border-red-800',
  admin: 'bg-orange-900/40 text-orange-400 border-orange-800',
  supervisor: 'bg-yellow-900/40 text-yellow-400 border-yellow-800',
  agent: 'bg-blue-900/40 text-blue-400 border-blue-800',
  user: 'bg-gray-800 text-gray-400 border-gray-700',
};

// Traducción de errores de validación del backend a mensajes claros
const FIELD_LABELS = {
  username: 'Nombre de usuario', email: 'Email', password: 'Contraseña',
  role: 'Rol', firstName: 'Nombre', lastName: 'Apellido',
};
const FIELD_HINTS = {
  username: 'solo letras, números y . _ - (sin espacios), mínimo 3 caracteres',
  email: 'formato de email inválido',
  password: 'mínimo 8 caracteres',
  firstName: 'no puede estar vacío',
  lastName: 'no puede estar vacío',
};
function describeError(e) {
  const errs = e.response?.data?.errors;
  if (Array.isArray(errs) && errs.length) {
    return errs.map(x => `${FIELD_LABELS[x.path] || x.path}: ${FIELD_HINTS[x.path] || x.msg}`).join(' · ');
  }
  return e.response?.data?.error || 'Error al guardar';
}

export default function UsersPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [perms, setPerms] = useState({}); // overrides por usuario {cap: bool}
  const { register, handleSubmit, reset, setValue, watch, formState: { isSubmitting, errors } } = useForm();
  const roleSel = watch('role') || 'user';

  const { data = [], isLoading } = useQuery('users', () => api.get('/users').then(r => r.data));

  // Extensiones para el selector del formulario (limit alto: por defecto la API pagina de 50 en 50)
  const { data: extList = [] } = useQuery('extensions-for-users',
    () => api.get('/extensions?limit=500').then(r => r.data?.data || []), { staleTime: 60000 });

  // Catálogo de capacidades + defaults por rol
  const { data: catalog } = useQuery('perm-catalog', () => api.get('/users/permissions/catalog').then(r => r.data));
  const roleDefaults = catalog?.roleDefaults || {};

  // Estado efectivo de una capacidad: override del usuario > default del rol
  const effective = (key) => {
    if (Object.prototype.hasOwnProperty.call(perms, key)) return !!perms[key];
    if (roleSel === 'superadmin') return true;
    return (roleDefaults[roleSel] || []).includes(key);
  };
  const toggleCap = (key) => setPerms(p => ({ ...p, [key]: !effective(key) }));

  const saveMut = useMutation(
    (d) => {
      // Al editar, una contraseña vacía significa "no cambiarla": si se enviara
      // el campo vacío el backend intentaría fijarla y devolvería un error.
      if (editing && !d.password) { const { password, ...resto } = d; d = resto; }
      return editing ? api.put('/users/' + editing.id, d) : api.post('/users', d);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('users');
        toast.success(editing ? 'Usuario actualizado' : 'Usuario creado');
        closeForm();
      },
      onError: (e) => toast.error(describeError(e)),
    }
  );

  const disableMut = useMutation(
    (id) => api.delete('/users/' + id),
    {
      onSuccess: () => { qc.invalidateQueries('users'); toast.success('Usuario desactivado'); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error'),
    }
  );

  // Reactivar un usuario desactivado
  const enableMut = useMutation(
    (id) => api.put('/users/' + id, { isActive: true }),
    {
      onSuccess: () => { qc.invalidateQueries('users'); toast.success('Usuario reactivado'); },
      onError: (e) => toast.error(describeError(e)),
    }
  );

  // Desbloquear cuenta bloqueada por intentos fallidos de acceso
  const unlockMut = useMutation(
    (id) => api.post('/users/' + id + '/unlock'),
    {
      onSuccess: (r) => { qc.invalidateQueries('users'); toast.success(r?.data?.message || 'Cuenta desbloqueada'); },
      onError: (e) => toast.error(e.response?.data?.error || 'No se pudo desbloquear'),
    }
  );

  // Borrado DEFINITIVO (no reversible)
  const deleteMut = useMutation(
    (id) => api.delete('/users/' + id + '/permanent'),
    {
      onSuccess: (r) => { qc.invalidateQueries('users'); toast.success(r?.data?.message || 'Usuario eliminado'); },
      onError: (e) => toast.error(e.response?.data?.error || 'No se pudo eliminar'),
    }
  );

  const openEdit = (u) => {
    setEditing(u);
    setValue('firstName', u.first_name);
    setValue('lastName', u.last_name);
    setValue('email', u.email);
    setValue('role', u.role);
    setValue('isActive', u.is_active);
    setValue('extensionId', u.extension_id || '');
    setPerms(u.permissions || {});
    setShowForm(true);
  };

  const openNew = () => { setEditing(null); reset(); setPerms({}); setValue('role', 'agent'); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setPerms({}); reset(); };
  const onSubmit = (d) => saveMut.mutate({ ...d, extensionId: d.extensionId || null, permissions: perms });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuarios</h1>
          <p className="text-gray-400 text-sm">{data.length} usuarios registrados</p>
        </div>
        <button onClick={openNew} className="btn-primary">
          <Plus size={18} /> Nuevo Usuario
        </button>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {['Usuario', 'Nombre', 'Email', 'Rol', 'Extension', 'Ultimo acceso', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(u => (
                  <tr key={u.id} className="table-row">
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-600/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-400">
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <span className="font-medium text-gray-200">{u.username}</span>
                      </div>
                    </td>
                    <td className="table-td">{u.first_name} {u.last_name}</td>
                    <td className="table-td text-xs text-gray-400">{u.email}</td>
                    <td className="table-td">
                      <span className={'text-xs px-2 py-0.5 rounded-full border font-medium ' + (ROLE_COLORS[u.role] || ROLE_COLORS.user)}>
                        {u.role}
                      </span>
                    </td>
                    <td className="table-td font-mono text-gray-400">{u.extension_number || '-'}</td>
                    <td className="table-td text-xs text-muted">
                      {u.last_login ? new Date(u.last_login).toLocaleString('es') : 'Nunca'}
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-1.5">
                        <span className={u.is_active ? 'badge-success' : 'badge-danger'}>
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                        {u.is_locked && (
                          <span className="badge-danger inline-flex items-center gap-1" title="Bloqueado por intentos fallidos de acceso">
                            <Lock size={11} /> Bloqueado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="flex gap-1">
                        {u.is_locked && (
                          <button title="Desbloquear (intentos fallidos de acceso)"
                            onClick={() => unlockMut.mutate(u.id)}
                            className="p-1.5 text-amber-400 hover:text-amber-300 rounded-lg">
                            <Unlock size={15} />
                          </button>
                        )}
                        <button onClick={() => openEdit(u)} aria-label={`Editar usuario ${u.username}`}
                          className="p-1.5 text-gray-400 hover:text-blue-400 rounded-lg">
                          <Edit2 size={15} />
                        </button>
                        {u.is_active ? (
                          <button title="Desactivar (reversible)"
                            onClick={() => { if (window.confirm('¿Desactivar a ' + u.username + '?\n\nNo podrá entrar, pero se conserva su histórico y puedes reactivarlo.')) disableMut.mutate(u.id); }}
                            className="p-1.5 text-gray-400 hover:text-yellow-400 rounded-lg">
                            <UserX size={15} />
                          </button>
                        ) : (
                          <button title="Reactivar"
                            onClick={() => enableMut.mutate(u.id)}
                            className="p-1.5 text-gray-400 hover:text-green-400 rounded-lg">
                            <UserCheck size={15} />
                          </button>
                        )}
                        <button title="Eliminar definitivamente"
                          onClick={() => {
                            if (window.confirm('¿ELIMINAR DEFINITIVAMENTE a ' + u.username + '?\n\nEsta acción NO se puede deshacer. Si solo quieres impedirle el acceso, usa Desactivar.')) {
                              deleteMut.mutate(u.id);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
              <h2 className="text-lg font-semibold text-white">
                {editing ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button onClick={closeForm} aria-label="Cerrar"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              {!editing && (
                <div>
                  <label className="label" htmlFor="user-username">Nombre de usuario *</label>
                  {(() => {
                    const r = register('username', {
                      required: 'Requerido',
                      minLength: { value: 3, message: 'Mínimo 3 caracteres' },
                      pattern: { value: /^[a-z0-9._-]+$/, message: 'Solo minúsculas, números y . _ - (sin espacios)' },
                    });
                    return (
                      <input
                        id="user-username"
                        {...r}
                        onChange={(e) => { e.target.value = e.target.value.toLowerCase().replace(/\s+/g, ''); r.onChange(e); }}
                        className="input" placeholder="jperez" autoComplete="off" />
                    );
                  })()}
                  {errors.username
                    ? <p className="text-xs text-red-400 mt-1">{errors.username.message}</p>
                    : <p className="text-xs text-muted mt-1">Se usa para iniciar sesión. Minúsculas, sin espacios. Solo letras, números y . _ - (ej: <span className="text-gray-400">jperez</span>, <span className="text-gray-400">leader_1</span>)</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="user-first-name">Nombre *</label>
                  <input id="user-first-name" {...register('firstName', { required: true })} className="input" placeholder="Juan" />
                </div>
                <div>
                  <label className="label" htmlFor="user-last-name">Apellido *</label>
                  <input id="user-last-name" {...register('lastName', { required: true })} className="input" placeholder="Perez" />
                </div>
              </div>
              <div>
                <label className="label" htmlFor="user-email">Email *</label>
                <input id="user-email" {...register('email', { required: true })} type="email" className="input" placeholder="juan@empresa.com" />
              </div>
              <div>
                <label className="label" htmlFor="user-password">Contraseña {editing ? '' : '*'}</label>
                <input
                  id="user-password"
                  {...register('password', {
                    required: !editing ? 'Requerido' : false,
                    minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                  })}
                  type="password" className="input" autoComplete="new-password"
                  placeholder={editing ? 'Dejar en blanco para no cambiarla' : 'Mín. 8 caracteres'} />
                {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
                {editing && (
                  <p className="text-xs text-muted mt-1">
                    Escribe una nueva contraseña solo si quieres cambiarla.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="user-role">Rol</label>
                  <select id="user-role" {...register('role')} className="input">
                    <option value="user">Usuario</option>
                    <option value="agent">Agente</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Administrador</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </div>
                {editing && (
                  <div>
                    <label className="label" htmlFor="user-is-active">Estado</label>
                    <select id="user-is-active" {...register('isActive')} className="input">
                      <option value="true">Activo</option>
                      <option value="false">Inactivo</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="label" htmlFor="user-extension">Extensión</label>
                <select id="user-extension" {...register('extensionId')} className="input">
                  <option value="">Sin extensión</option>
                  {extList.map(x => {
                    const otros = data.filter(u => u.extension_id === x.id && u.id !== editing?.id).map(u => u.username);
                    return (
                      <option key={x.id} value={x.id}>
                        {x.number}{x.caller_id_name ? ` · ${x.caller_id_name}` : ''}{otros.length ? ` — en uso por ${otros.join(', ')}` : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-muted mt-1">
                  Sin extensión, el usuario no verá el Marcador. Si la extensión ya la usa otro usuario, solo uno podrá estar conectado a la vez.
                </p>
              </div>
              {/* Checklist de permisos */}
              {catalog && roleSel !== 'superadmin' && (
                <div className="pt-2 border-t border-gray-800">
                  <div className="flex items-center justify-between mb-1">
                    <span className="label mb-0">Permisos</span>
                    <button type="button" onClick={() => setPerms({})}
                      className="text-xs text-blue-400 hover:text-blue-300">
                      ↻ Restablecer al rol «{roleSel}»
                    </button>
                  </div>
                  <p className="text-xs text-muted mb-3">
                    Marca las funcionalidades que este usuario puede usar. Por defecto hereda las del rol; los cambios son overrides.
                  </p>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {catalog.capabilities.map(grp => (
                      <div key={grp.group}>
                        <p className="text-[11px] uppercase tracking-wider text-muted mb-1.5">{grp.group}</p>
                        <div className="space-y-1">
                          {grp.items.map(item => {
                            const on = effective(item.key);
                            const overridden = Object.prototype.hasOwnProperty.call(perms, item.key);
                            return (
                              <label key={item.key}
                                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-800/60 cursor-pointer">
                                <input type="checkbox" checked={on} onChange={() => toggleCap(item.key)}
                                  className="w-4 h-4 rounded accent-blue-600 shrink-0" />
                                <span className={`text-sm ${on ? 'text-gray-200' : 'text-muted'}`}>{item.label}</span>
                                {overridden && <span className="text-[11px] text-amber-500/80">· personalizado</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {roleSel === 'superadmin' && (
                <div className="pt-2 border-t border-gray-800 text-xs text-muted">
                  El rol <strong className="text-gray-300">Super Admin</strong> tiene acceso completo a todo el sistema.
                </div>
              )}

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-gray-900 pb-1">
                <button type="button" onClick={closeForm} className="btn-secondary flex-1 justify-center">Cancelar</button>
                <button type="submit" disabled={isSubmitting} className="btn-primary flex-1 justify-center">
                  {isSubmitting ? <><div className="spinner" /> Guardando...</> : (editing ? 'Actualizar' : 'Crear Usuario')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
